import React, { useState, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from '@mellify/ui';
import { PlatformIcon } from './PlatformIcon';
import { Shield, ExternalLink, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useToast } from '../../../../hooks/useToast';
import { apiClient, extractData } from '../../../../services/api';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';

interface ConnectBlueskyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface BlueskyConnectResponse {
  status: 'connected';
  account: {
    id: string;
    platform: string;
    platformAccountId: string;
    accountName: string | null;
    accountHandle: string | null;
    accountType: string;
    avatarUrl: string | null;
    status: string;
    followerCount: number;
  };
}

export const ConnectBlueskyModal: React.FC<ConnectBlueskyModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [identifier, setIdentifier] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toast = useToast();
  const queryClient = useQueryClient();
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);

  const handleConnect = useCallback(async () => {
    if (!identifier.trim()) {
      setError('Please enter your Bluesky handle.');
      return;
    }

    if (!appPassword.trim()) {
      setError('Please enter your Bluesky app password.');
      return;
    }

    if (!currentWorkspace?.id) {
      setError('No workspace selected. Please select a workspace first.');
      return;
    }

    setError(null);
    setIsConnecting(true);

    try {
      const result = await extractData(
        apiClient.post<BlueskyConnectResponse>('/social-accounts/connect/bluesky', {
          workspaceId: currentWorkspace.id,
          identifier: identifier.trim(),
          appPassword: appPassword.trim(),
        })
      );

      toast.success(
        'Bluesky Connected!',
        `@${result.account.accountHandle || result.account.accountName} has been connected successfully.`
      );

      // Invalidate social accounts queries to refetch
      queryClient.invalidateQueries({ queryKey: ['social-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['social-account-health'] });

      // Reset form
      setIdentifier('');
      setAppPassword('');
      setError(null);

      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to connect Bluesky account.';
      setError(message);
      toast.error('Connection Failed', message);
    } finally {
      setIsConnecting(false);
    }
  }, [identifier, appPassword, currentWorkspace, toast, queryClient, onSuccess, onClose]);

  const handleClose = useCallback(() => {
    if (!isConnecting) {
      setIdentifier('');
      setAppPassword('');
      setError(null);
      onClose();
    }
  }, [isConnecting, onClose]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      closeOnOverlayClick={!isConnecting}
      closeOnEscape={!isConnecting}
      aria-labelledby="connect-bluesky-title"
    >
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-dark-700 border border-dark-200 dark:border-dark-600">
            <PlatformIcon platform="bluesky" size="md" />
          </div>
          <div>
            <h2
              id="connect-bluesky-title"
              className="text-lg font-semibold text-dark-900 dark:text-dark-100"
            >
              Connect Bluesky
            </h2>
            <p className="text-xs text-dark-500 dark:text-dark-400 mt-0.5">
              Sign in with your Bluesky app password
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Security notice */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 mb-5">
          <Shield className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
              App Password Required
            </p>
            <p className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
              Bluesky uses app passwords for third-party integrations. This is{' '}
              <strong>not</strong> your main account password. Create an app password
              in your{' '}
              <a
                href="https://bsky.app/settings/app-passwords"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primary-700 dark:hover:text-primary-200"
              >
                Bluesky settings
              </a>
              .
            </p>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 mb-5">
            <AlertTriangle className="w-5 h-5 text-danger-500 shrink-0 mt-0.5" />
            <p className="text-sm text-danger-700 dark:text-danger-300">{error}</p>
          </div>
        )}

        {/* Form fields */}
        <div className="space-y-4">
          <div>
            <label
              htmlFor="bluesky-handle"
              className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1.5"
            >
              Bluesky Handle
            </label>
            <Input
              id="bluesky-handle"
              type="text"
              placeholder="username.bsky.social"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              disabled={isConnecting}
              autoComplete="username"
            />
            <p className="text-xs text-dark-400 dark:text-dark-500 mt-1">
              Your full Bluesky handle (e.g., username.bsky.social or custom domain)
            </p>
          </div>

          <div>
            <label
              htmlFor="bluesky-app-password"
              className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1.5"
            >
              App Password
            </label>
            <div className="relative">
              <Input
                id="bluesky-app-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="xxxx-xxxx-xxxx-xxxx"
                value={appPassword}
                onChange={(e) => setAppPassword(e.target.value)}
                disabled={isConnecting}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-600 dark:hover:text-dark-300"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            <p className="text-xs text-dark-400 dark:text-dark-500 mt-1">
              Create an app password at{' '}
              <a
                href="https://bsky.app/settings/app-passwords"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-500 hover:text-primary-600 underline"
              >
                bsky.app/settings/app-passwords
              </a>
            </p>
          </div>
        </div>

        {/* Permissions list */}
        <div className="mt-5 pt-4 border-t border-dark-200 dark:border-dark-700">
          <h3 className="text-sm font-medium text-dark-700 dark:text-dark-300 mb-3">
            Mellify will be able to:
          </h3>
          <div className="space-y-2">
            {[
              { label: 'Read Profile', desc: 'View your Bluesky profile information and feed' },
              { label: 'Create Posts', desc: 'Create and publish posts on your behalf' },
              { label: 'Read Followers', desc: 'Access your follower and following data' },
              { label: 'Manage Content', desc: 'Like, repost, and interact with content' },
            ].map((perm, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="flex items-center justify-center w-4 h-4 rounded-full bg-success-50 dark:bg-success-900/20 shrink-0 mt-0.5">
                  <svg className="w-2.5 h-2.5 text-success-500" viewBox="0 0 12 12" fill="none">
                    <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-dark-800 dark:text-dark-200">
                    {perm.label}
                  </p>
                  <p className="text-xs text-dark-500 dark:text-dark-400">
                    {perm.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Data usage note */}
        <div className="mt-4 pt-3 border-t border-dark-200 dark:border-dark-700">
          <p className="text-xs text-dark-400 dark:text-dark-500">
            Your app password is securely encrypted and stored. You can revoke access at any time
            from the Channels page or by deleting the app password in your Bluesky settings.
          </p>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          variant="outline"
          size="md"
          onClick={handleClose}
          disabled={isConnecting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleConnect}
          loading={isConnecting}
          disabled={!identifier.trim() || !appPassword.trim()}
          rightIcon={!isConnecting ? <ExternalLink className="w-4 h-4" /> : undefined}
        >
          {isConnecting ? 'Connecting...' : 'Connect'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

ConnectBlueskyModal.displayName = 'ConnectBlueskyModal';